// app/javascript/controllers/dae20/template_picker_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Template Picker Controller
 *
 * Manages the template selection UI in the component editor:
 * - Toggle between normal center column content and visual template picker
 * - Text input for direct code entry (resolves code → id)
 * - Click to select from visual grid (sets id + code + label)
 * - Search/filter within the picker grid
 */
export default class extends Controller {
  static targets = ["content", "picker", "input", "hiddenId", "label", "search", "list"]

  connect() {
    // Build code→id and id→{code,name} lookup maps from picker buttons
    this.codeToId = {}
    this.idToInfo = {}

    const buttons = this.element.querySelectorAll("[data-template-id]")
    buttons.forEach(btn => {
      const id = btn.dataset.templateId
      const code = btn.dataset.templateCode
      const name = btn.dataset.templateName
      if (code && id) {
        this.codeToId[code] = id
        this.idToInfo[id] = { code, name }
      }
    })
  }

  // Toggle picker visibility
  toggle() {
    const isOpen = !this.pickerTarget.classList.contains("hidden")
    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.contentTarget.classList.add("hidden")
    this.pickerTarget.classList.remove("hidden")
    // Focus search input
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
      this._showAll()
    }
  }

  close() {
    this.pickerTarget.classList.add("hidden")
    this.contentTarget.classList.remove("hidden")
  }

  // User selected a template from the picker grid
  select(event) {
    const btn = event.currentTarget
    const id = btn.dataset.templateId
    const code = btn.dataset.templateCode
    const name = btn.dataset.templateName

    // Update hidden field (actual form value)
    if (this.hasHiddenIdTarget) {
      this.hiddenIdTarget.value = id
    }

    // Update text input (shows code)
    if (this.hasInputTarget) {
      this.inputTarget.value = code
    }

    // Update label
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = name
      this.labelTarget.title = name
      this.labelTarget.classList.remove("hidden")
    }

    // Update selected state in picker
    this._updateSelectedState(id)

    // Close picker
    this.close()
  }

  // User typed a code directly into the text input
  codeChanged(event) {
    const code = event.target.value.trim()
    const id = this.codeToId[code]

    if (id) {
      // Known code → set ID and update label
      if (this.hasHiddenIdTarget) {
        this.hiddenIdTarget.value = id
      }
      const info = this.idToInfo[id]
      if (this.hasLabelTarget && info) {
        this.labelTarget.textContent = info.name
        this.labelTarget.title = info.name
        this.labelTarget.classList.remove("hidden")
      }
    } else {
      // Unknown code → pass as-is (might be a raw ID or new code)
      if (this.hasHiddenIdTarget) {
        this.hiddenIdTarget.value = code
      }
      if (this.hasLabelTarget) {
        this.labelTarget.classList.add("hidden")
      }
    }
  }

  // Filter picker cards by search text
  filter(event) {
    const query = (event.target.value || "").toLowerCase().trim()
    if (!query) {
      this._showAll()
      return
    }

    const cards = this.element.querySelectorAll("[data-searchable]")
    const groups = this.element.querySelectorAll("[data-picker-group]")

    cards.forEach(card => {
      const text = card.dataset.searchable || ""
      card.classList.toggle("hidden", !text.includes(query))
    })

    // Hide groups with no visible cards
    groups.forEach(group => {
      const visibleCards = group.querySelectorAll("[data-searchable]:not(.hidden)")
      group.classList.toggle("hidden", visibleCards.length === 0)
    })
  }

  // --- Private ---

  _showAll() {
    this.element.querySelectorAll("[data-searchable]").forEach(el => el.classList.remove("hidden"))
    this.element.querySelectorAll("[data-picker-group]").forEach(el => el.classList.remove("hidden"))
  }

  _updateSelectedState(selectedId) {
    const buttons = this.element.querySelectorAll("[data-template-id]")
    buttons.forEach(btn => {
      const isSelected = btn.dataset.templateId === selectedId
      // Reset all
      btn.classList.remove("border-blue-500", "bg-blue-50", "ring-1", "ring-blue-500")
      btn.classList.add("border-gray-200", "bg-white")
      // Remove old badge
      const badge = btn.querySelector("[data-selected-badge]")
      if (badge) badge.remove()

      if (isSelected) {
        btn.classList.add("border-blue-500", "bg-blue-50", "ring-1", "ring-blue-500")
        btn.classList.remove("border-gray-200", "bg-white")
        // Add badge
        const badgeEl = document.createElement("div")
        badgeEl.setAttribute("data-selected-badge", "")
        badgeEl.className = "absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center"
        badgeEl.innerHTML = '<svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>'
        btn.appendChild(badgeEl)
      }
    })
  }
}
